import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { clearPendingReading } from "@/lib/pendingReading";
import { track, identify, resetAnalytics, flushBeforeRedirect, planOf, CONSENT_EVENT } from "@/lib/analytics";
import { rememberReturnTo, clearReturnTo } from "@/lib/returnTo";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entitlements, setEntitlements] = useState(null);
  // "idle" (logged out/unknown) | "loading" | "ready" | "error"
  const [entitlementsStatus, setEntitlementsStatus] = useState("idle");
  const oauthReturn = useRef(window.location.hash?.includes("session_id="));

  const refreshEntitlements = useCallback(async () => {
    setEntitlementsStatus("loading");
    try {
      const res = await api.get("/entitlements");
      setEntitlements(res.data);
      setEntitlementsStatus("ready");
      return res.data;
    } catch (e) {
      // Preserve any previously known entitlement state on a transient failure —
      // do NOT null it out, or the UI would wrongly fall back to the free/Upgrade state.
      setEntitlementsStatus("error");
      return null;
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      await refreshEntitlements();
    } catch (e) {
      setUser(null);
      setEntitlements(null);
      setEntitlementsStatus("idle");
    } finally {
      setLoading(false);
    }
  }, [refreshEntitlements]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // Read during the first render: AuthCallback (a child) strips the hash in its own effect, which runs first.
    if (oauthReturn.current) {
      setLoading(false);
      return;
    }
    // A fresh, non-OAuth page load means any earlier sign-in attempt was cancelled or failed.
    clearReturnTo();
    checkAuth();
  }, [checkAuth]);

  const plan = entitlements ? planOf(entitlements) : null;
  useEffect(() => {
    const sync = () => { if (user?.user_id && plan) identify(user.user_id, plan); };
    sync();
    window.addEventListener(CONSENT_EVENT, sync);
    return () => window.removeEventListener(CONSENT_EVENT, sync);
  }, [user?.user_id, plan]);

  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const pageLabel = () => (window.location.pathname === "/" ? "home" : window.location.pathname.slice(1));

  // Opens the sign-in choice (Google or email) and remembers where the visitor was.
  const login = () => {
    rememberReturnTo();
    setAuthDialogOpen(true);
  };

  const closeAuthDialog = () => {
    setAuthDialogOpen(false);
    if (!user) clearReturnTo();
  };

  // Called after any successful sign-in (email login or email verification).
  const completeSignIn = async (signedInUser, method) => {
    setUser(signedInUser);
    setAuthDialogOpen(false);
    const ent = await refreshEntitlements();
    identify(signedInUser.user_id, planOf(ent));
    track("sign_in_completed", { plan: planOf(ent), method });
  };

  const loginWithGoogle = async () => {
    track("sign_in_started", { page: pageLabel(), method: "google" });
    await flushBeforeRedirect();
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.error("Logout request failed", err);
    }
    clearPendingReading();
    clearReturnTo();
    resetAnalytics();
    setUser(null);
    setEntitlements(null);
    setEntitlementsStatus("idle");
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, loading, checkAuth, login, logout, loginWithGoogle, completeSignIn,
               authDialogOpen, closeAuthDialog, trackEmailStart: () => track("sign_in_started", { page: pageLabel(), method: "email" }),
               entitlements, entitlementsStatus, refreshEntitlements }}
    >
      {children}
    </AuthContext.Provider>
  );
};
